import React, { useRef, useState } from "react";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import PaymentProvider from "../payment/PaymentProvider";
import { SecurityIcon } from "../icons/Icons";
import PlanSummary from "./PlanSummary";
import LoadingIndicator from "../LoadingIndicator";

export default function PaymentStep({
  pricingData,
  isPosting = false,
  onSubmitPayment,
  showPaypal,
  subscriptionPlan,
  isInviteFlow,
  quantity,
  isFlatPricing
}) {
  const { totalPrice } = pricingData || {};
  const formik = useFormikContext();
  const { values } = formik;
  const { t } = useTranslation("signup");
  const tapRef = useRef(null);
  const [isTapReady, setIsTapReady] = useState(false);
  const [isCardValid, setIsCardValid] = useState(false);
  const [isTokenizing, setIsTokenizing] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [tapError, setTapError] = useState(null);

  const isPayDisabled =
    isTokenizing ||
    isPosting ||
    isRedirecting ||
    ((values.payment_provider || "tap") === "tap"
      ? !isTapReady || !isCardValid
      : false);

  const handlePayClick = async () => {
    if (isPayDisabled) return;

    const provider = values.payment_provider || "tap";

    if (provider === "tap") {
      if (tapRef.current) {
        setIsTokenizing(true);
        setTapError(null);
        try {
          const tokenId = await tapRef.current.tokenize();
          
          setIsRedirecting(true);
          
          await onSubmitPayment?.({
            payment_provider: provider,
            payment_token: tokenId,
          });
        } catch (error) {
          console.error("Tokenization/Payment failed:", error);
          setIsRedirecting(false);
          const errorMsg = error?.message || t(
            "payment_tokenization_failed",
            "We couldn't process your card details. Please check the information and try again.",
          );
          setTapError(errorMsg);
          toast.error(errorMsg);
        } finally {
          setIsTokenizing(false);
        }
      }
    } else {
      setIsRedirecting(true);
      try {
        await onSubmitPayment?.({ payment_provider: provider, payment_token: null });
      } catch (error) {
        setIsRedirecting(false);
      }
    }
  };

  // Optional: handle SDK errors surfaced outside of tokenize() (e.g. inline errors)
  const handleTokenizationError = (error) => {
    console.error("Tap Payment Error:", error);
    setTapError(
      t(
        "payment_tokenization_failed",
        "We couldn't process your card details. Please check the information and try again.",
      ),
    );
  };

  return (
    <div className="step-content payment">
      <PaymentProvider
        formik={formik}
        tapRef={tapRef}
        showPaypal={showPaypal}
        transaction={{ amount: totalPrice, currency: "KWD" }}
        onTokenizationError={handleTokenizationError}
        onTapReady={(value) => {
          setIsTapReady(value);
          if (!value) setIsCardValid(false); // reset card valid when SDK reloads
          setTapError(null);
        }}
        onCardValid={(isValid) => {
          setIsCardValid(isValid);
          if (isValid) setTapError(null);
        }}
      >
        {tapError && (
          <div className="grid-full payment-error-toast desktop">
            {tapError}
          </div>
        )}
        <div className="pay-now-bottom grid-full desktop">
          {isPosting || isTokenizing || isRedirecting ? (
            <LoadingIndicator />
          ) : (
            <button
              type="button"
              className={`tad-btn pay-now ${(formik.values.payment_provider || "tap") === "paypal" ? "paypal-btn" : ""} ${isPayDisabled ? "disabled" : ""}`}
              onClick={handlePayClick}
              disabled={isPayDisabled}
            >
              {(formik.values.payment_provider || "tap") === "tap" && !isTapReady
                ? t("loading", "Loading...")
                : (formik.values.payment_provider || "tap") === "paypal"
                  ? <span style={{ fontWeight: 400 }}><span style={{ fontStyle: "italic", fontWeight: "900", fontFamily: "Arial, sans-serif" }}>PayPal</span> {t("pay_with", "دفع بواسطة")}</span>
                  : t("pay_now")}
            </button>
          )}
          <div className="security">
            <SecurityIcon />
            <p>{t("secure_payment_note")}</p>
          </div>
        </div>
      </PaymentProvider>
      <div className="payment-summary">
        <PlanSummary
          planType={subscriptionPlan?.plan_type}
          pricingData={pricingData}
          quantity={quantity}
          isBasketStep={false}
          isInviteFlow={isInviteFlow}
          isFlatPricing={isFlatPricing}
        />
        </div>

      <div className="mob-button-action"></div>
      <div className="pay-now-bottom grid-full mob">
        {tapError && <div className="payment-error-toast mob">{tapError}</div>}
        {isPosting || isTokenizing || isRedirecting ? (
          <LoadingIndicator />
        ) : (
          <button
            type="button"
            className={`tad-btn pay-now ${(formik.values.payment_provider || "tap") === "paypal" ? "paypal-btn" : ""} ${isPayDisabled ? "disabled" : ""}`}
            onClick={handlePayClick}
            disabled={isPayDisabled}
          >
            {(formik.values.payment_provider || "tap") === "tap" && !isTapReady
              ? t("loading", "Loading...")
              : (formik.values.payment_provider || "tap") === "paypal"
                ? <span>{t("pay_with", "دفع بواسطة")} <span style={{ fontStyle: "italic", fontWeight: "900", fontFamily: "Arial, sans-serif" }}>PayPal</span></span>
                : t("pay_now")}
          </button>
        )}
        <div className="security">
          <SecurityIcon />
          <p>{t("secure_payment_note")}</p>
        </div>
      </div>
    </div>
  );
}
